import React, { useCallback } from 'react';
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Heading,
    Input
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';
import { useTranslate } from 'ts/common/hooks';
import { VALID_EMAIL_REGEX } from 'ts/common/utils';
import type { QuestionnaireContext } from 'client_react/questionnaire/QuestionnaireApp';

interface EmailFormValues {
    email: string;
}

const QuestionnaireEmailForm: React.FC = () => {
    const t = useTranslate('clientQuestionnaire');

    const { fetchQuestionnaire, loading, error } = useOutletContext<QuestionnaireContext>();

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<EmailFormValues>({
        defaultValues: { email: '' },
        mode: 'onSubmit'
    });

    const onSubmit = useCallback(
        async (data: EmailFormValues) => {
            await fetchQuestionnaire(data.email.trim());
        },
        [fetchQuestionnaire]
    );

    const fieldError = errors.email?.message;
    const serverError =
        error?.status === 403 ? t('emailIncorrectError') : (error?.detail as string | undefined);
    const displayError = fieldError ?? serverError;

    return (
        <Flex flexDirection="column" alignItems="center">
            <Heading as="h3" size="md" fontSize="20px" mb={6} textAlign="center">
                {t('emailHeading')}
            </Heading>
            <Flex
                as="form"
                onSubmit={handleSubmit(onSubmit)}
                flexDirection="column"
                alignItems="center"
                maxWidth="350px"
                width="100%"
            >
                <FormControl isInvalid={!!displayError} isRequired mb={4}>
                    <FormLabel htmlFor="email">{t('email')}</FormLabel>
                    <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        {...register('email', {
                            required: t('fieldRequired'),
                            pattern: {
                                value: VALID_EMAIL_REGEX,
                                message: t('emailInvalidError')
                            }
                        })}
                    />
                    <FormErrorMessage>{displayError}</FormErrorMessage>
                </FormControl>
                <Button type="submit" variant="primary" isLoading={loading}>
                    {t('viewQuestionnaire')}
                </Button>
            </Flex>
        </Flex>
    );
};

export default QuestionnaireEmailForm;
